import { Component, Fragment, type ReactNode } from "react";
import { ErrorFallback, type ErrorLevel } from "./ErrorFallback";
import { ErrorReportingService } from "@/services/errorReporting";

interface ErrorBoundaryProps {
  componentName: string;
  level?: ErrorLevel;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorKey: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorKey: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const platform = ErrorReportingService.detectPlatform();
    const environment = ErrorReportingService.getEnvironment();

    this.setState({ errorInfo });

    ErrorReportingService.reportError({
      componentName: this.props.componentName,
      platform,
      timestamp: Date.now(),
      error,
      errorInfo,
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
      userAgent: typeof window !== "undefined" ? window.navigator.userAgent : undefined,
      environment,
    });

    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorKey: prevState.errorKey + 1,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          level={this.props.level || "feature"}
          onReset={this.handleReset}
          componentName={this.props.componentName}
        />
      );
    }

    return <Fragment key={this.state.errorKey}>{this.props.children}</Fragment>;
  }
}
